import { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import xls from "xlsx";
import moment from "moment";
import { useLocation } from "react-router-dom";
import { getFunds } from "../stores/services/funds.service";
import { addProvidentFundsBulkUploader } from "../stores/services/provident-funds.service";
import { getAllUnitHoldersByCompany } from "../stores/services/unit-holder.service";
import { getAllCompanies } from "../stores/services/company.service";
import { getNewChartsOfAccountsByFund } from "../stores/services/chatofaccounts.service";
import { getLedgerAccounts } from "../utils/customFunction";

export const usePFFundUploader = () => {
  const location = useLocation();
  const email = sessionStorage.getItem("email") || "";
  const amc_code = sessionStorage.getItem("amc_code") || "";
  const period = sessionStorage.getItem("active_period") || "";

  // Determine upload type based on URL
  const isInitialContribution = location.pathname.includes(
    "initial-contribution",
  );
  const isMonthlyContribution = location.pathname.includes(
    "monthly-contribution",
  );

  // File state
  const [file, setFile] = useState<any>("");
  const [fileName, setFileName] = useState<any>("");
  const [fileData, setfileData] = useState<any>([]);
  const [fileDataCount, setFileDataCount] = useState<any>(0);
  const [displayLimit, setDisplayLimit] = useState<number>(500);
  const [fileValidationErrors, setFileValidationErrors] = useState<any[]>([]);
  const myRef1 = useRef<any>(null);

  // Fund and company state
  const [fund, setFund] = useState<any>("");
  const [loading, setLoading] = useState<any>(false);
  const [allFunds, setAllFunds] = useState<any>([]);
  const [allEmployee, setAllEmployee] = useState<any>([]);
  const [companyOptions, setCompanyOptions] = useState<any>([]);
  const [companyName, setCompanyName] = useState<any>(null);
  const [fundLoading, setFundLoading] = useState<any>(false);
  const [loadingCompany, setLoadingCompany] = useState<any>(false);

  // Account state
  const [fundBankAccount, setFundAccount] = useState<any>({});
  const [counterFundLoading, setCounterFundLoading] = useState(false);
  const [allCounterFundsAccounts, setAllCounterFundsAccounts] = useState<any>(
    [],
  );
  const [chartOfAccounts, setChartOfAccounts] = useState<any>([]);

  // Error state
  const [ErrorModal, setErrorModal] = useState<any>(false);
  const [errorInData, setErrorInData] = useState<any>([]);

  // ======================== DATA FETCH ========================

  const getAllFunds = async () => {
    if (!companyName?.value) return;
    try {
      const res = await getFunds({ email, company_name: companyName.value });
      setAllFunds(res.data.data || []);
    } catch (error) {
      console.log("fund fetch error", error);
    }
  };

  const getAllEmployee = async () => {
    try {
      const res = await getAllUnitHoldersByCompany({
        email,
        company_code: companyName.value,
      });
      if (res?.status === 200 && res?.data?.status === 200) {
        const temp = res.data.data.map(
          (u) => `${u.folio_no.replace("UNITHOLDER_", "")}`,
        );
        setAllEmployee(temp);
      }
    } catch (err) {
      console.log("employee fetch error", err);
    }
  };

  const fetchAllCompanies = async () => {
    try {
      setLoadingCompany(true);
      const resp = await getAllCompanies({ email, fetch_all: "all" });
      if (resp.data.status === 200) {
        const options = resp.data.data.map((c) => ({
          value: c.company_code,
          label: `${c.name_of_company} (${c.company_code})`,
        }));
        setCompanyOptions(options);
      }
    } finally {
      setLoadingCompany(false);
    }
  };

  const getCounterFundAccountByFundName = async (fund_code: string) => {
    setCounterFundLoading(true);
    try {
      let response = await getNewChartsOfAccountsByFund(
        email,
        amc_code,
        fund_code,
      );
      if (response.data.status === 200) {
        setChartOfAccounts(response?.data?.data?.main_family);
        const counterAccounts = getLedgerAccounts(
          response?.data?.data?.main_family[0].family[3].sub_family,
        );
        let temp = counterAccounts.map((val) => ({
          ...val,
          label: `${val.account_title} (${val.account_no})`,
          value: val,
        }));
        temp.unshift({ value: "Other", label: "Other" });
        setAllCounterFundsAccounts(temp);
      } else {
        setAllCounterFundsAccounts([{ value: "Other", label: "Other" }]);
      }
    } catch (error) {
      console.error("Error fetching counter fund accounts:", error);
    } finally {
      setCounterFundLoading(false);
    }
  };

  // ======================== EFFECTS ========================

  useEffect(() => {
    const load = async () => {
      setFundLoading(true);
      await fetchAllCompanies();
      setFundLoading(false);
    };
    load();
  }, []);

  useEffect(() => {
    if (companyName?.value) {
      getAllFunds();
      getAllEmployee();
    }
  }, [companyName]);

  useEffect(() => {
    if (fund) getCounterFundAccountByFundName(fund);
  }, [fund]);

  // ======================== FILE PROCESSING ========================

  const fixMalformedRow = (row) => {
    const keys = Object.keys(row);
    if (keys.length === 1 && keys[0].startsWith("__EMPTY")) {
      const parts = row[keys[0]].split(/[,\s]+/).filter(Boolean);
      if (isInitialContribution) {
        return {
          "Fund Code": parts[0] || "",
          Date: parts[1] || "",
          Month: parts[2] || "",
          "Employee Code": parts[3] || "",
          "Employee Name": parts[4] || "",
          CNIC: parts[5] || "",
          "Employee Contribution": parts[6] || "",
          "Employer Contribution": parts[7] || "",
          Profit: parts[8] || "",
          LoanAmount: parts[9] || "",
          LoanReturned: parts[10] || "",
          LoanRemaining: parts[11] || "",
          "No of Installments": parts[12] || "",
          "Total Amount": parts[13] || "",
          Remarks: "Initial Contributions",
        };
      } else {
        return {
          "Fund Code": parts[0] || "",
          Date: parts[1] || "",
          Month: parts[2] || "",
          "Employee Code": parts[3] || "",
          "Employee Name": parts[4] || "",
          CNIC: parts[5] || "",
          "Employee Contribution": parts[6] || "",
          "Employer Contribution": parts[7] || "",
          Adjustment: parts[8] || "",
          "Loan Installment": parts[9] || "",
          Total: parts[10] || "",
          Remarks: parts.slice(11).join(" ") || "",
        };
      }
    }

    const emptyCols = keys.filter((k) => k.startsWith("__EMPTY"));
    if (emptyCols.length === keys.length) {
      const joined = emptyCols
        .map((c) => row[c])
        .join(" ")
        .trim()
        .split(/[,\s]+/);
      if (isInitialContribution) {
        return {
          "Fund Code": joined[0] || "",
          Date: joined[1] || "",
          Month: joined[2] || "",
          "Employee Code": joined[3] || "",
          "Employee Name": joined[4] || "",
          CNIC: joined[5] || "",
          "Employee Contribution": joined[6] || "",
          "Employer Contribution": joined[7] || "",
          Profit: joined[8] || "",
          LoanAmount: joined[9] || "",
          LoanReturned: joined[10] || "",
          LoanRemaining: joined[11] || "",
          "No of Installments": joined[12] || "",
          "Total Amount": joined[13] || "",
          Remarks: "Initial Contributions",
        };
      } else {
        return {
          "Fund Code": joined[0] || "",
          Date: joined[1] || "",
          Month: joined[2] || "",
          "Employee Code": joined[3] || "",
          "Employee Name": joined[4] || "",
          CNIC: joined[5] || "",
          "Employee Contribution": joined[6] || "",
          "Employer Contribution": joined[7] || "",
          Adjustment: joined[8] || "",
          "Loan Installment": joined[9] || "",
          Total: joined[10] || "",
          Remarks: joined.slice(11).join(" ") || "",
        };
      }
    }
    return row;
  };

  const validateFileData = (data: any[]) => {
    const validationErrors: any[] = [];

    data.forEach((r, idx) => {
      if (r["Fund Code"]?.trim() !== fund) {
        validationErrors.push({
          row: idx + 2,
          field: "Fund Code",
          value: r["Fund Code"],
          error: `Fund code mismatch. Expected: ${fund}, Found: ${r["Fund Code"]}`,
        });
      }
      if (!r["Employee Code"]?.trim()) {
        validationErrors.push({
          row: idx + 2,
          field: "Employee Code",
          value: "",
          error: "Employee Code cannot be empty",
        });
      }
      if (!r["Employee Name"]?.trim()) {
        validationErrors.push({
          row: idx + 2,
          field: "Employee Name",
          value: "",
          error: "Employee Name cannot be empty",
        });
      }
    });

    const empCodeMap = new Map();
    data.forEach((r, idx) => {
      const empCode = r["Employee Code"];
      if (empCode) {
        if (empCodeMap.has(empCode)) {
          validationErrors.push({
            row: idx + 2,
            field: "Employee Code",
            value: empCode,
            error: `Duplicate Employee Code. Also found at row ${empCodeMap.get(
              empCode,
            )}`,
          });
        } else {
          empCodeMap.set(empCode, idx + 2);
        }
      }
    });

    const cnicMap = new Map();
    data.forEach((r, idx) => {
      const cnic = r.CNIC;
      if (cnic) {
        if (cnicMap.has(cnic)) {
          validationErrors.push({
            row: idx + 2,
            field: "CNIC",
            value: cnic,
            error: `Duplicate CNIC. Also found at row ${cnicMap.get(cnic)}`,
          });
        } else {
          cnicMap.set(cnic, idx + 2);
        }
      }
    });

    data.forEach((r, idx) => {
      const empCode = r["Employee Code"]?.toUpperCase()?.trim();
      const fullCode = `${companyName.value}-${empCode}`;
      if (empCode && !allEmployee.includes(fullCode)) {
        validationErrors.push({
          row: idx + 3,
          field: "Employee Code",
          value: empCode,
          error: `Invalid Employee Code. Not found in system for company ${companyName.value}`,
        });
      }
    });

    return validationErrors;
  };

  const upload = (e) => {
    const fileObj = e.target.files[0];
    if (!fileObj) return;

    const ext = fileObj.name.split(".").pop().toLowerCase();
    if (ext !== "xlsx" && ext !== "csv") {
      toast.error("Invalid file format");
      return;
    }

    setFile(fileObj);
    setFileName(fileObj.name);
    setFileValidationErrors([]);
    setDisplayLimit(500);

    const reader = new FileReader();
    const rABS = !!reader.readAsBinaryString;

    reader.onload = (ev: any) => {
      const wb = xls.read(ev.target.result, {
        type: rABS ? "binary" : "array",
      });
      let final: any = [];

      wb.SheetNames.forEach((sheet) => {
        const raw = xls.utils.sheet_to_json(wb.Sheets[sheet], {
          header: 2,
          raw: false,
          defval: "",
        });
        const processed = raw
          .map(fixMalformedRow)
          .map((obj) =>
            Object.fromEntries(
              Object.entries(obj).filter(([k]) => !k.startsWith("__EMPTY")),
            ),
          );
        final = processed;
      });

      if (!final.length) {
        toast.error("File empty");
        return;
      }

      const validationErrors = validateFileData(final);
      if (validationErrors.length > 0) {
        setFileValidationErrors(validationErrors);
        toast.error(
          `Found ${validationErrors.length} validation error(s). Please review below.`,
        );
        setfileData([]);
        setFileDataCount(0);
        return;
      }

      setfileData(final);
      setFileDataCount(final.length);
      toast.success(`File loaded successfully! ${final.length} records found.`);
    };

    if (rABS) reader.readAsBinaryString(fileObj);
    else reader.readAsArrayBuffer(fileObj);
    e.target.value = "";
  };

  // ======================== LEDGER LOGIC ========================

  const getLedgerInfoByLedgerCode = (coa, ledgerCode) => {
    let obj = {};
    coa.forEach((item: any) => {
      item.family.forEach((familyF: any) => {
        familyF.sub_family.forEach((subfamily: any) => {
          if (subfamily.child_family) {
            subfamily.child_family.forEach((itm: any) => {
              itm.gl.forEach((glItem: any) => {
                if (Number(glItem.code) === Number(ledgerCode)) {
                  obj = {
                    gl_code: glItem.code,
                    gl_name: glItem.name,
                    sub_family_code: subfamily.code,
                    sub_family_name: subfamily.name,
                    main_family_code: item.code,
                    main_family_name: item.name,
                    family_code: familyF.code,
                    family_name: familyF.name,
                    child_family_code: itm.code,
                    child_family_name: itm.name,
                  };
                }
              });
            });
          } else {
            subfamily.gl.forEach((glItem: any) => {
              if (Number(glItem.code) === Number(ledgerCode)) {
                obj = {
                  gl_code: glItem.code,
                  gl_name: glItem.name || glItem.account_title,
                  sub_family_code: subfamily.code,
                  sub_family_name: subfamily.name,
                  main_family_code: item.code,
                  main_family_name: item.name,
                  family_code: familyF.code,
                  family_name: familyF.name,
                  child_family_code: 0,
                  child_family_name: "",
                };
              }
            });
          }
        });
      });
    });
    return obj;
  };

  const createLedgerEntry = (code, type, amount, amountType, dateStr) => {
    const info: any = getLedgerInfoByLedgerCode(chartOfAccounts, code);
    return {
      main_family_code: info?.main_family_code,
      main_family_name: info?.main_family_name,
      family_code: info?.family_code,
      family_name: info?.family_name,
      sub_family_code: info?.sub_family_code,
      sub_family_name: info?.sub_family_name,
      child_family_code: info?.child_family_code,
      child_family_name: info?.child_family_name,
      gl_code: info?.gl_code,
      gl_name: info?.gl_name,
      amount_type: amountType,
      type: type,
      amc_code: amc_code,
      fund_code: fund,
      account_number: type === "Debit" ? fundBankAccount.account_no : "",
      account_title: type === "Debit" ? fundBankAccount.account_title : "",
      bank_code: type === "Debit" ? fundBankAccount.bank : "",
      branch: type === "Debit" ? fundBankAccount.branch : "",
      balance: "",
      counter_account_number: "",
      counter_account_title: "",
      counter_bank: "",
      counter_branch: "",
      credit_amount: type === "Credit" ? amount.toFixed(4) : "0",
      debit_amount: type === "Debit" ? amount.toFixed(4) : "0",
      description: `${amountType} - ${dateStr}`,
      entered_by: "",
      evidence: "",
      instruction_date: dateStr,
      instrument_by: "",
      instrument_date: dateStr,
      instrument_no: "",
      instrument_type: "",
      realized: "true",
      realized_by: "",
      realized_date: dateStr,
      period: period,
    };
  };

  // ======================== UPLOAD LOGIC ========================

  const handleUploadClick = async () => {
    if (!fileData?.length) {
      toast.error("No file processed");
      return;
    }
    if (!fund) {
      toast.error("Please select fund");
      return;
    }
    if (!fundBankAccount) {
      toast.error("Please select fund bank account");
      return;
    }
    if (!companyName.value) {
      toast.error("Please select company code");
      return;
    }

    setLoading(true);

    try {
      const getCommaRemove = (num: string): string => {
        return String(num).trim().replaceAll(",", "");
      };

      let payload;
      let totalEmployeeContribution = 0;
      let totalEmployerContribution = 0;
      let totalProfit = 0;
      let totalLoanInstallment = 0;
      let total_loan_amount,
        total_loan_returned = 0;

      if (isInitialContribution) {
        payload = fileData.map((ele: any) => ({
          symbol_code: ele["Fund Code"],
          month_of_contribution: ele.Month
            ? moment(ele.Month, ["MMM-YY", "MMM-YYYY", "MM-YYYY"]).format(
                "YYYY-MM-DD",
              )
            : "",
          folio_no: `${companyName.value}-${ele["Employee Code"]}`,
          employee_name: ele["Employee Name"],
          employee_cnic: ele.CNIC,
          employee_contribution: parseFloat(
            getCommaRemove(ele["Employee Contribution"]),
          ),
          employer_contribution: parseFloat(
            getCommaRemove(ele["Employer Contribution"]),
          ),
          profit: parseFloat(getCommaRemove(ele["Profit"] || "0")),
          loan_amount: parseFloat(getCommaRemove(ele["LoanAmount"])),
          loan_returned: parseFloat(getCommaRemove(ele["LoanReturned"])),
          loan_remaining: parseFloat(getCommaRemove(ele["LoanRemaining"])),
          loan_installments: parseFloat(
            getCommaRemove(ele["No of Installments"]),
          ),
          total: parseFloat(getCommaRemove(ele["Total Amount"])),
          remarks: "Initial Contribution",
        }));

        totalEmployeeContribution = fileData.reduce(
          (acc, row) =>
            acc +
            (parseFloat(getCommaRemove(row["Employee Contribution"])) || 0),
          0,
        );
        totalEmployerContribution = fileData.reduce(
          (acc, row) =>
            acc +
            (parseFloat(getCommaRemove(row["Employer Contribution"])) || 0),
          0,
        );
        totalProfit = fileData.reduce(
          (acc, row) =>
            acc + (parseFloat(getCommaRemove(row["Profit"] || "0")) || 0),
          0,
        );
        total_loan_amount = fileData.reduce(
          (acc: number, row) =>
            acc + parseFloat(getCommaRemove(row["LoanAmount"] || "0")),
          0,
        );
        total_loan_returned = fileData.reduce(
          (acc: number, row) =>
            acc + parseFloat(getCommaRemove(row["LoanReturned"] || "0")),
          0,
        );
      } else {
        payload = fileData.map((ele: any) => ({
          symbol_code: ele["Fund Code"],
          month_of_contribution: ele.Month
            ? moment(ele.Month, ["MMM-YY", "MMM-YYYY", "MM-YYYY"]).format(
                "YYYY-MM-DD",
              )
            : "",
          folio_no: `${companyName.value}-${ele["Employee Code"]}`,
          employee_name: ele["Employee Name"],
          employee_cnic: ele.CNIC,
          employee_contribution: parseFloat(
            getCommaRemove(ele["Employee Contribution"]),
          ),
          employer_contribution: parseFloat(
            getCommaRemove(ele["Employer Contribution"]),
          ),
          adjustment: parseFloat(
            String(ele.Adjustment).trim().replaceAll(",", "") || "0",
          ),
          loan_adjustment: ele["Loan Adjustment"] || "",
          loan_installment:
            parseFloat(getCommaRemove(ele["Loan Installment"])) || "",
          total: parseFloat(
            getCommaRemove(ele["Total Amount"] || ele["Total"]),
          ),
          remarks: ele.Remarks,
        }));

        totalEmployeeContribution = fileData.reduce(
          (acc, row) =>
            acc +
            (parseFloat(getCommaRemove(row["Employee Contribution"])) || 0),
          0,
        );
        totalEmployerContribution = fileData.reduce(
          (acc, row) =>
            acc +
            (parseFloat(getCommaRemove(row["Employer Contribution"])) || 0),
          0,
        );
        totalLoanInstallment = fileData.reduce(
          (acc, row) =>
            acc + (parseFloat(getCommaRemove(row["Loan Installment"])) || 0),
          0,
        );
      }

      const employee_gl_code = 301030701;
      const employeer_gl_code = 301030702;
      const loan_gl_code = 10208030701;

      const dateStr = moment().format("YYYY-MM-DD");
      const settlement_date = payload[0].month_of_contribution
        ? payload[0].month_of_contribution
        : dateStr;
      let txnLedgers: any[] = [];

      let liabilityCode = null;
      if (fundBankAccount.related_ledgers) {
        for (const n of fundBankAccount.related_ledgers) {
          if (String(n).startsWith("5")) {
            liabilityCode = n;
            break;
          }
        }
      }

      if (chartOfAccounts.length > 0) {
        if (isInitialContribution) {
          const grossAmount =
            totalEmployeeContribution + totalEmployerContribution + totalProfit;

          txnLedgers.push(
            createLedgerEntry(
              fundBankAccount.code,
              "Debit",
              grossAmount,
              "Gross Amount",
              dateStr,
            ),
          );
          txnLedgers.push(
            createLedgerEntry(
              employee_gl_code,
              "Credit",
              totalEmployeeContribution,
              "Employee Contribution",
              dateStr,
            ),
          );
          txnLedgers.push(
            createLedgerEntry(
              employeer_gl_code,
              "Credit",
              totalEmployerContribution,
              "Employer Contribution",
              dateStr,
            ),
          );
          if (totalProfit > 0 && liabilityCode) {
            txnLedgers.push(
              createLedgerEntry(
                liabilityCode,
                "Credit",
                totalProfit,
                "Accrued Profit on Bank Deposit",
                dateStr,
              ),
            );
          }

          if (total_loan_amount > 0) {
            txnLedgers.push(
              createLedgerEntry(
                loan_gl_code,
                "Credit",
                total_loan_amount,
                "Loan Given",
                dateStr,
              ),
            );
          }

          if (total_loan_returned > 0) {
            txnLedgers.push(
              createLedgerEntry(
                loan_gl_code,
                "Debit",
                total_loan_returned,
                "Loan Returned",
                dateStr,
              ),
            );
          }
        } else {
          const grossAmount =
            totalEmployeeContribution +
            totalEmployerContribution +
            totalLoanInstallment;

          txnLedgers.push(
            createLedgerEntry(
              fundBankAccount.code,
              "Debit",
              grossAmount,
              "Gross Amount",
              dateStr,
            ),
          );
          txnLedgers.push(
            createLedgerEntry(
              employee_gl_code,
              "Credit",
              totalEmployeeContribution,
              "Employee Contribution",
              dateStr,
            ),
          );
          txnLedgers.push(
            createLedgerEntry(
              employeer_gl_code,
              "Credit",
              totalEmployerContribution,
              "Employer Contribution",
              dateStr,
            ),
          );
          if (totalLoanInstallment > 0) {
            txnLedgers.push(
              createLedgerEntry(
                loan_gl_code,
                "Credit",
                totalLoanInstallment,
                "Loan Installment",
                dateStr,
              ),
            );
          }
        }
      }

      const grossAmount = isInitialContribution
        ? totalEmployeeContribution + totalEmployerContribution + totalProfit
        : totalEmployeeContribution +
          totalEmployerContribution +
          totalLoanInstallment;

      const detailText = isInitialContribution
        ? `Provident Fund Initial Deposit - Employee Contribution: ${totalEmployeeContribution.toFixed(
            2,
          )}, Employer Contribution: ${totalEmployerContribution.toFixed(
            2,
          )}, Profit: ${totalProfit.toFixed(2)}`
        : `Provident Fund Monthly Deposit - Employee Contribution: ${totalEmployeeContribution.toFixed(
            2,
          )}, Employer Contribution: ${totalEmployerContribution.toFixed(
            2,
          )}, Loan Installment: ${totalLoanInstallment.toFixed(2)}`;

      const txn_obj = {
        email: email,
        txnr_type: "fundcontribution",
        amc_code: amc_code,
        fund_code: fund,
        instruction_date: dateStr,
        execution_date: dateStr,
        executed_date: dateStr,
        fund_account: fundBankAccount?.code?.toString() || "",
        reinvest: false,
        account_title: fundBankAccount?.account_title || "",
        period: period,
        account_no: fundBankAccount?.account_no || "",
        bank: fundBankAccount?.bank || "",
        branch: fundBankAccount?.branch || "",
        counter_account_type: "",
        counter_type: "",
        counter_account_title: "",
        counter_account_number: "",
        counter_bank: "",
        counter_branch: "",
        mode_of_payment: "Bank Transfer",
        payment_type: "Receipt",
        instrument_no: "",
        instrument_type: "",
        instrument_date: dateStr,
        realized_date: dateStr,
        realized: true,
        gross_amount: grossAmount,
        total_charges: 0,
        txn_charges: 0,
        net_amount: grossAmount,
        ledgers: JSON.stringify(txnLedgers),
        settlement_date,
        settlement_time: new Date(settlement_date).getTime(),
        settlement_by: email,
        txn_status: "CREATED",
        detail: detailText,
      };

      console.log(payload);

      const res = await addProvidentFundsBulkUploader(
        email,
        companyName?.value || "",
        payload,
        txn_obj,
      );

      if (res?.data?.status === 200) {
        toast.success("Uploaded successfully");
        resetForm();
      } else if (res?.data?.status === 207) {
        setErrorInData(res.data.data.errors || []);
        setErrorModal(true);
      } else {
        toast.error("Upload failed");
      }
    } catch (err) {
      toast.error("Server error");
      console.error("Upload error:", err);
    } finally {
      setLoading(false);
    }
  };

  // ======================== UTILITIES ========================

  const handleLoadMore = () => {
    setDisplayLimit((prev) => prev + 500);
  };

  const resetForm = () => {
    setfileData([]);
    setFileDataCount(0);
    setFileName("");
    setFund("");
    setFundAccount({});
    setCompanyName(null);
    setFileValidationErrors([]);
    setDisplayLimit(500);
  };

  const renderFundsDropdown = () =>
    allFunds.map((item) => ({
      value: item.symbol_code,
      label:
        item.nature === "Special Managed Account"
          ? `${item.fund_name} (${item.symbol_code}) - SMA`
          : `${item.fund_name} (${item.symbol_code})`,
    }));

  const selectedFundName = allFunds.find((item) => item.symbol_code === fund)
    ? {
        value: fund,
        label:
          allFunds.find((item) => item.symbol_code === fund)?.nature ===
          "Special Managed Account"
            ? `${
                allFunds.find((item) => item.symbol_code === fund)?.fund_name
              } (${fund}) - SMA`
            : `${
                allFunds.find((item) => item.symbol_code === fund)?.fund_name
              } (${fund})`,
      }
    : null;

  return {
    // State
    file,
    fileName,
    fileData,
    fileDataCount,
    displayLimit,
    fileValidationErrors,
    fund,
    loading,
    allFunds,
    companyOptions,
    companyName,
    fundLoading,
    loadingCompany,
    fundBankAccount,
    counterFundLoading,
    allCounterFundsAccounts,
    ErrorModal,
    errorInData,
    myRef1,
    isInitialContribution,
    isMonthlyContribution,

    // Setters
    setFund,
    setCompanyName,
    setFundAccount,
    setErrorModal,

    // Functions
    upload,
    handleUploadClick,
    handleLoadMore,
    resetForm,
    renderFundsDropdown,
    selectedFundName,
  };
};
